import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthImageSide from "./AuthImageSide";
import logo from "../../assets/images/isotipo-invecasa.svg";
import "./AuthPage.css";
import { login, forgotPassword } from "../../api/authApi";
import { useAuth } from "../../context/AuthContext";

import {
  hasDifferentLoggedUser,
  saveAuthMeta,
} from "../../context/AuthSync";

async function encryptData(data, key) {
  const enc = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = enc.encode(data);
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    encoded
  );
  return {
    iv: Array.from(iv),
    data: Array.from(new Uint8Array(encrypted)),
  };
}

async function decryptData(encrypted, key) {
  const dec = new TextDecoder();
  const iv = new Uint8Array(encrypted.iv);
  const data = new Uint8Array(encrypted.data);
  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    data
  );
  return dec.decode(decrypted);
}

async function getCryptoKey(secret) {
  const enc = new TextEncoder();
  const rawKey = enc.encode(secret.padEnd(32));
  return crypto.subtle.importKey("raw", rawKey, "AES-GCM", false, [
    "encrypt",
    "decrypt",
  ]);
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { login: authLogin, authenticated } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [showRecoveryConfirmation, setShowRecoveryConfirmation] =
    useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryError, setRecoveryError] = useState("");

  useEffect(() => {
    const stored = localStorage.getItem("rememberedEncryptedLogin");
    if (stored) {
      (async () => {
        try {
          const { email: encEmail, password: encPassword } = JSON.parse(stored);
          const key = await getCryptoKey("remember-secret-key");
          const decryptedEmail = await decryptData(encEmail, key);
          const decryptedPassword = await decryptData(encPassword, key);
          setEmail(decryptedEmail);
          setPassword(decryptedPassword);
          setRememberMe(true);
        } catch (e) {
          console.error("Error decrypting stored credentials", e);
        }
      })();
    }
  }, []);

  useEffect(() => {
    if (authenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [authenticated, navigate]);

  async function handleSubmit(e) {
    e.preventDefault();

    if (!email || !password) {
      setErrorMessage("Please enter your email and password.");
      return;
    }

    if (hasDifferentLoggedUser(email)) {
      setErrorMessage("");
      navigate("/dashboard", { replace: true });
      return;
    }

    setLoading(true);
    try {
      const response = await login({ email, password });

      authLogin(response.token);

      saveAuthMeta({ email });

      if (rememberMe) {
        const key = await getCryptoKey("remember-secret-key");
        const encryptedEmail = await encryptData(email, key);
        const encryptedPassword = await encryptData(password, key);
        localStorage.setItem(
          "rememberedEncryptedLogin",
          JSON.stringify({
            email: encryptedEmail,
            password: encryptedPassword,
          })
        );
      } else {
        localStorage.removeItem("rememberedEncryptedLogin");
      }

      navigate("/dashboard");
    } catch (err) {
      setErrorMessage("Please check your credentials or sign-in method.");
    } finally {
      setLoading(false);
    }
  }

  async function handleSendRecoveryEmail() {
    try {
      const data = await forgotPassword(recoveryEmail);

      if (data.message?.toLowerCase().includes("recovery link")) {
        setRecoveryError("");
        setShowRecoveryModal(false);
        setShowRecoveryConfirmation(true);
      } else {
        setRecoveryError("An error occurred. Please try again later.");
      }
    } catch (err) {
      const code = err?.data?.code;
      if (code === "PR001") {
        setRecoveryError("Invalid email address.");
      } else {
        setRecoveryError("An error occurred. Please try again later.");
      }
    }
  }

  function clearError() {
    if (errorMessage) setErrorMessage("");
  }

  function handleGoogleLogin() {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = `${process.env.REACT_APP_URL}/google/callback`;
    const scope = "openid email profile";
    const responseType = "code";

    const googleUrl =
      `https://accounts.google.com/o/oauth2/v2/auth` +
      `?response_type=${responseType}` +
      `&client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent(scope)}` +
      `&access_type=offline` +
      `&prompt=consent`;

    window.location.href = googleUrl;
  }

  function openRecoveryModal() {
    setRecoveryEmail("");
    setRecoveryError("");
    setShowRecoveryModal(true);
  }

  function closeRecoveryModal() {
    setShowRecoveryModal(false);
    setRecoveryEmail("");
    setRecoveryError("");
  }

  function closeRecoveryConfirmation() {
    setShowRecoveryConfirmation(false);
  }

  return (
    <div className="authpage">
      <AuthImageSide />
      <div className="authpage__form-container">
        <div className="authpage__card">
          <div className="authpage__logo">
            <img src={logo} alt="Logo" />
          </div>
          <h2 className="authpage__title">Welcome back to INVESCASA</h2>
          <p className="authpage__subtitle">
            All the data you need to make an informed decision
          </p>
          <form onSubmit={handleSubmit}>
            <div className="authpage__input-warning-wrap">
              <input
                type="email"
                className={`authpage__input ${
                  email ? "authpage__input--filled" : ""
                }`}
                placeholder="Email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearError();
                }}
                autoComplete="off"
              />
            </div>
            <div className="authpage__input-warning-wrap authpage__input-password-wrap">
              <input
                type={showPassword ? "text" : "password"}
                className={`authpage__input ${
                  password ? "authpage__input--filled" : ""
                }`}
                placeholder="Password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  clearError();
                }}
                autoComplete="off"
              />
              {password && (
                <button
                  type="button"
                  className="authpage__input-visibility-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 20 20">
                      <path
                        d="M1 10c2.5-4.5 6.5-7 9-7s6.5 2.5 9 7c-2.5 4.5-6.5 7-9 7s-6.5-2.5-9-7z"
                        fill="none"
                        stroke="#8493ad"
                        strokeWidth="1.5"
                      />
                      <circle
                        cx="10"
                        cy="10"
                        r="3"
                        fill="none"
                        stroke="#8493ad"
                        strokeWidth="1.5"
                      />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 20 20">
                      <path
                        d="M1 10c2.5-4.5 6.5-7 9-7s6.5 2.5 9 7c-2.5 4.5-6.5 7-9 7s-6.5-2.5-9-7z"
                        fill="none"
                        stroke="#8493ad"
                        strokeWidth="1.5"
                      />
                      <circle
                        cx="10"
                        cy="10"
                        r="3"
                        fill="none"
                        stroke="#8493ad"
                        strokeWidth="1.5"
                      />
                      <line
                        x1="5"
                        y1="15"
                        x2="15"
                        y2="5"
                        stroke="#8493ad"
                        strokeWidth="1.5"
                      />
                    </svg>
                  )}
                </button>
              )}
            </div>
            {errorMessage && (
              <p
                style={{
                  color: "#bdbdbd",
                  fontSize: "0.9rem",
                  marginBottom: "12px",
                }}
              >
                {errorMessage}
              </p>
            )}
            <span
              onClick={openRecoveryModal}
              className="authpage__forgot"
              role="button"
              tabIndex={0}
            >
              Forgot password?
            </span>
            <div className="authpage__remember-row">
              <label className="authpage__remember-label" htmlFor="rememberMe">
                Remember log in details
              </label>
              <label className="authpage__switch">
                <input
                  id="rememberMe"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                />
                <span className="authpage__slider"></span>
              </label>
            </div>
            <button
              className="authpage__login-btn"
              type="submit"
              disabled={loading}
            >
              {loading ? "Logging in..." : "Log in"}
            </button>
            <button
              className="authpage__google-btn"
              type="button"
              onClick={handleGoogleLogin}
            >
              <img
                src="https://www.svgrepo.com/show/475656/google-color.svg"
                alt="Google"
                className="authpage__google-logo"
              />
              Continue with Google
            </button>
          </form>
          <div className="authpage__signup">
            Don’t have an account?
            <Link to="/signup" className="authpage__signup-link">
              {" "}
              Sign up{" "}
            </Link>
          </div>
        </div>
      </div>

      {showRecoveryModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h3 className="modal-title">Recover your password</h3>
            <p className="modal-text">
              Enter your email to receive a recovery link.
            </p>
            <input
              type="email"
              placeholder="Enter your email"
              value={recoveryEmail}
              onChange={(e) => {
                setRecoveryEmail(e.target.value);
              }}
              className="authpage__input"
              style={{ marginBottom: "10px" }}
              required
              onInvalid={(e) =>
                e.target.setCustomValidity("Please enter a valid email address.")
              }
              onInput={(e) => e.target.setCustomValidity("")}
            />
            {recoveryError && (
              <p
                style={{
                  color: "#bdbdbd",
                  fontSize: "0.9rem",
                  marginBottom: "16px",
                }}
              >
                {recoveryError}
              </p>
            )}
            <div className="modal-buttons">
              <button className="modal-btn cancel" onClick={closeRecoveryModal}>
                Cancel
              </button>
              <button
                className="modal-btn confirm"
                onClick={handleSendRecoveryEmail}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {showRecoveryConfirmation && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h3 className="modal-title">Check your inbox</h3>
            <p className="modal-text">
              We’ve sent you a link to reset your password. The link is valid
              for 30 minutes. Please remember to check your spam folder.
            </p>
            <div className="modal-buttons">
              <button
                className="modal-btn confirm"
                onClick={closeRecoveryConfirmation}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
